import React, { useState, useRef } from "react";
import C from "classnames";

// Toolkit components
import { themeClasses as T } from "../../utils/themeClasses";
import TopPanel, { TopPanelProps } from "./TopPanel";
import Drawer, { DrawerProps } from "./Drawer";

// Constants
const GutterResizingClass = "p-splitter-gutter-resizing";

// Styles
import S from "./_styles.module.scss";

// Props interface
interface PanelWithDrawerProps {
  children?: React.ReactNode;
  className?: string;
  resizable?: boolean;
}

// Main component
const PanelWithDrawers: React.FC<PanelWithDrawerProps> = ({
  children,
  className = "",
  resizable = false,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [deltaHeight, setDeltaHeight] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (_e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(true);
  };

  const handleMouseUp = (_e: React.MouseEvent<HTMLDivElement>) => {
    setIsResizing(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isResizing && gutterRef.current) {
      e.preventDefault();
      const gutterMidpoint =
        gutterRef.current.getBoundingClientRect().top + gutterRef.current.offsetHeight / 2;
      const delta = gutterMidpoint - e.clientY;
      setDeltaHeight(delta);
    }
  };

  return (
    <div
      className={C(
        T.splitterGap,
        "p-splitter p-component p-splitter-vertical",
        className,
        isResizing ? "p-splitter-resizing" : ""
      )}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      {/* Render the top panel */}
      {React.Children.map(children, child => {
        if (React.isValidElement<TopPanelProps>(child) && child.type === TopPanel) {
          return React.cloneElement(child, {
            panelRef,
            isResizing,
          });
        }
        return null;
      })}

      {/* Render the divider/gutter */}
      {resizable && (
        <div
          className={C("p-splitter-gutter", S.divider, { [GutterResizingClass]: isResizing })}
          onMouseDown={handleMouseDown}
          ref={gutterRef}
        >
          <div className="p-splitter-gutter-handle" aria-hidden="true" />
        </div>
      )}

      {/* Render the bottom panel(s) */}
      {React.Children.map(children, child => {
        if (React.isValidElement<DrawerProps>(child) && child.type === Drawer) {
          return React.cloneElement(child, {
            isResizing,
            deltaHeight,
          });
        }
        return null;
      })}
    </div>
  );
};

export default PanelWithDrawers;
